import React, { useState } from "react";
import api, { pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Confidence, Empty } from "@/components/common";
import { toast } from "sonner";
import { Sparkles, Check, X, Share2, Copy, FileQuestion, ArrowRight, Loader2 } from "lucide-react";

const PRIORITY_CLS = {
  "Must ask": "bg-red-500/15 text-red-600 dark:text-red-400",
  "Should ask": "bg-[#c9a227]/15 text-[#a6821a] dark:text-[#e0b838]",
  "Nice to ask": "bg-secondary text-muted-foreground",
};

export default function PrepareStep({ meeting, refresh, setActive, mid }) {
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});
  const mapping = meeting.module_mapping || [];
  const questions = meeting.questionnaire || [];

  const analyze = async () => {
    if (!meeting.agenda?.length) { toast.error("Add agenda items in Plan first"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/meetings/${mid}/analyze-agenda`);
      await pollJob(res.data.job_id);
      toast.success("AI mapping & questionnaire generated");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "AI analysis failed"); }
    finally { setBusy(false); }
  };

  const decide = async (id, accepted) => {
    await api.post(`/meetings/${mid}/mapping-decision`, { map_id: id, accepted });
    refresh();
  };

  const answer = async (q) => {
    const val = answers[q.id];
    if (!val) return;
    await api.post(`/meetings/${mid}/answer`, { question_id: q.id, answer: val });
    toast.success("Answer recorded");
    refresh();
  };

  const share = async () => {
    const res = await api.post(`/meetings/${mid}/share`);
    const url = `${window.location.origin}/share/${res.data.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Secure stakeholder link copied to clipboard");
  };

  if (mapping.length === 0 && questions.length === 0) {
    return (
      <Empty title="Generate pre-meeting intelligence"
        hint="Our SAP architect AI reads your agenda and landscape, maps likely modules, transactions and integration touchpoints, then drafts a stakeholder questionnaire."
        action={<Button onClick={analyze} disabled={busy} data-testid="analyze-agenda" className="gap-2">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />} {busy ? "Analyzing agenda…" : "Run AI pre-meeting analysis"}
        </Button>} />
    );
  }

  const answeredCount = questions.filter((q) => q.status === "answered").length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">{mapping.length} mapping cards · {questions.length} questions · {answeredCount} answered</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={analyze} disabled={busy} className="gap-2" data-testid="rerun-analyze">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} Re-run
          </Button>
          <Button variant="outline" onClick={share} className="gap-2" data-testid="share-questionnaire"><Share2 size={15} /> Share link</Button>
          <Button onClick={() => setActive("record")} className="gap-2">Next: Record <ArrowRight size={16} /></Button>
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3">AI Module Mapping</div>
        <div className="grid md:grid-cols-2 gap-4">
          {mapping.map((m) => (
            <div key={m.id} className={`rounded-xl border bg-card p-5 ${m.accepted === false ? "opacity-50" : ""}`} data-testid={`mapping-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-foreground">{m.process}</div>
                  <div className="text-xs text-muted-foreground">{m.agenda_item}</div>
                </div>
                <Confidence value={m.confidence} />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(m.modules || []).map((x) => <span key={x} className="text-[10px] font-semibold bg-[#0f2557]/10 dark:bg-[#29abe2]/15 text-[#0f2557] dark:text-[#29abe2] px-2 py-0.5 rounded">{x}</span>)}
              </div>
              {m.fiori_apps?.length > 0 && <div className="text-xs text-muted-foreground mt-2"><b>Apps:</b> {m.fiori_apps.join(", ")}</div>}
              {m.integration_touchpoints?.length > 0 && <div className="text-xs text-muted-foreground mt-1"><b>Integration:</b> {m.integration_touchpoints.join(", ")}</div>}
              <div className="text-xs text-muted-foreground mt-2 italic">{m.rationale}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant={m.accepted === true ? "default" : "outline"} onClick={() => decide(m.id, true)} className="gap-1 h-8" data-testid={`accept-${m.id}`}><Check size={13} /> Accept</Button>
                <Button size="sm" variant={m.accepted === false ? "destructive" : "outline"} onClick={() => decide(m.id, false)} className="gap-1 h-8"><X size={13} /> Reject</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase mb-3 flex items-center gap-2"><FileQuestion size={14} /> Pre-meeting Questionnaire</div>
        <div className="space-y-3">
          {questions.map((q) => (
            <div key={q.id} className="rounded-xl border bg-card p-5" data-testid={`question-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-medium text-foreground">{q.question}</div>
                  <div className="text-xs text-muted-foreground mt-1">{q.why_it_matters} <span className="text-[#29abe2]">· feeds {q.feeds_section}</span></div>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-1 rounded ${PRIORITY_CLS[q.priority] || "bg-secondary"}`}>{q.priority}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {(q.options || []).length > 0 ? (q.options || []).map((o) => (
                  <button key={o} onClick={() => setAnswers({ ...answers, [q.id]: o })}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition ${answers[q.id] === o || q.answer === o ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"}`}>{o}</button>
                )) : (
                  <Input className="max-w-md" placeholder="Answer…" value={answers[q.id] || q.answer || ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
                )}
                <Button size="sm" variant="outline" onClick={() => answer(q)} className="h-8" data-testid={`answer-${q.id}`}>Save</Button>
                {q.status === "answered" && <span className="text-xs text-emerald-600">✓ {q.answer}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
